import React, { useState } from "react";
import { Card, Tag, Typography } from "antd";
import { formatMinutes, getColor } from "../utils";
import { ProjectBreakdown, UserBreakdown } from "../types";

interface BreakdownTableProps {
  groupBy: "project" | "user";
  projectBreakdown: ProjectBreakdown[];
  userBreakdown: UserBreakdown[];
}

const ExpandIcon = ({ expanded }: { expanded: boolean }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 16,
      height: 16,
      flexShrink: 0,
      color: "#8c8c8c",
      fontSize: 10,
      transition: "transform 0.15s",
      transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
    }}
  >
    ▶
  </span>
);

const cellStyle: React.CSSProperties = {
  padding: "10px 16px",
  fontVariantNumeric: "tabular-nums",
};

const BreakdownTable = ({
  groupBy,
  projectBreakdown,
  userBreakdown,
}: BreakdownTableProps) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Card style={{ flex: 1 }} bodyStyle={{ padding: 0 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
            <th
              style={{
                ...cellStyle,
                textAlign: "left",
                fontSize: 12,
                color: "#8c8c8c",
                fontWeight: 600,
              }}
            >
              TITLE
            </th>
            <th
              style={{
                ...cellStyle,
                textAlign: "right",
                fontSize: 12,
                color: "#8c8c8c",
                fontWeight: 600,
              }}
            >
              DURATION
            </th>
          </tr>
        </thead>
        <tbody>
          {groupBy === "project"
            ? projectBreakdown.map((item, i) => {
                const id = item.projectId;
                const isExpanded = expanded.has(id);
                const color = getColor(item.color, item.projectName);
                return (
                  <React.Fragment key={id}>
                    <tr
                      style={{
                        borderBottom: "1px solid #f0f0f0",
                        cursor: item.users.length > 0 ? "pointer" : "default",
                        background: isExpanded ? "#fafafa" : undefined,
                      }}
                      onClick={() => item.users.length > 0 && toggle(id)}
                    >
                      <td
                        style={{
                          ...cellStyle,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {item.users.length > 0 ? (
                          <ExpandIcon expanded={isExpanded} />
                        ) : (
                          <span style={{ width: 16, flexShrink: 0 }} />
                        )}
                        <Tag
                          color="#137AE9"
                          style={{ margin: 0, fontWeight: 700 }}
                        >
                          {item.users.length}
                        </Tag>
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            background: color,
                            flexShrink: 0,
                            display: "inline-block",
                          }}
                        />
                        <Typography.Text strong>
                          {item.projectName}
                        </Typography.Text>
                        {item.clientName && (
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                          >
                            {item.clientName}
                          </Typography.Text>
                        )}
                      </td>
                      <td style={{ ...cellStyle, textAlign: "right" }}>
                        {formatMinutes(item.durationMinutes)}
                      </td>
                    </tr>
                    {isExpanded &&
                      item.users.map((user) => (
                        <tr
                          key={`${id}-${user.userId}`}
                          style={{
                            borderBottom: "1px solid #f0f0f0",
                            background: "#fafafa",
                          }}
                        >
                          <td
                            style={{
                              ...cellStyle,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              paddingLeft: 48,
                            }}
                          >
                            <Typography.Text style={{ fontSize: 13 }}>
                              {user.fullName}
                            </Typography.Text>
                          </td>
                          <td
                            style={{
                              ...cellStyle,
                              textAlign: "right",
                              color: "#595959",
                            }}
                          >
                            {formatMinutes(user.durationMinutes)}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })
            : userBreakdown.map((item) => {
                const id = item.userId;
                const isExpanded = expanded.has(id);
                return (
                  <React.Fragment key={id}>
                    <tr
                      style={{
                        borderBottom: "1px solid #f0f0f0",
                        cursor: "pointer",
                        background: isExpanded ? "#fafafa" : undefined,
                      }}
                      onClick={() => toggle(id)}
                    >
                      <td
                        style={{
                          ...cellStyle,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <ExpandIcon expanded={isExpanded} />

                        <Tag
                          color="#137AE9"
                          style={{ margin: 0, fontWeight: 700 }}
                        >
                          {item.projects.length}
                        </Tag>
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            background: getColor(undefined, item.fullName),
                            flexShrink: 0,
                            display: "inline-block",
                          }}
                        />
                        <Typography.Text strong>
                          {item.fullName}
                        </Typography.Text>
                        {item.approvedTimeOffDays > 0 && (
                          <Tag color="success" style={{ margin: 0 }}>
                            {item.approvedTimeOffDays} days off approved
                          </Tag>
                        )}
                        {item.pendingTimeOffDays > 0 && (
                          <Tag color="warning" style={{ margin: 0 }}>
                            {item.pendingTimeOffDays} days off pending
                          </Tag>
                        )}
                      </td>
                      <td style={{ ...cellStyle, textAlign: "right" }}>
                        {formatMinutes(item.durationMinutes)}
                      </td>
                    </tr>
                    {isExpanded &&
                      item.projects.map((proj, pi) => {
                        const projColor = getColor(
                          proj.color,
                          proj.projectName,
                        );
                        return (
                          <tr
                            key={`${id}-${proj.projectId}`}
                            style={{
                              borderBottom: "1px solid #f0f0f0",
                              background: "#fafafa",
                            }}
                          >
                            <td
                              style={{
                                ...cellStyle,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                paddingLeft: 48,
                              }}
                            >
                              <span
                                style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  background: projColor,
                                  flexShrink: 0,
                                  display: "inline-block",
                                }}
                              />
                              <Typography.Text style={{ fontSize: 13 }}>
                                {proj.projectName}
                              </Typography.Text>
                              {proj.clientName && (
                                <Typography.Text
                                  type="secondary"
                                  style={{ fontSize: 12 }}
                                >
                                  {proj.clientName}
                                </Typography.Text>
                              )}
                            </td>
                            <td
                              style={{
                                ...cellStyle,
                                textAlign: "right",
                                color: "#595959",
                              }}
                            >
                              {formatMinutes(proj.durationMinutes)}
                            </td>
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })}
        </tbody>
      </table>
    </Card>
  );
};

export default BreakdownTable;
